import { cloneDeep } from 'lodash'
import { getAddress } from 'viem'
import type { Config } from 'wagmi'
import { StoreApi } from 'zustand'
import type { LoadMaxAmount } from '@/dex/components/PagePool/Deposit/types'
import type { EstimatedGas as FormEstGas, Slippage } from '@/dex/components/PagePool/types'
import { type Amount, DEFAULT_SLIPPAGE } from '@/dex/components/PagePool/utils'
import { parseAmountsForAPI } from '@/dex/components/PagePool/utils'
import type { FormStatus, FormType, FormValues } from '@/dex/components/PagePool/Withdraw/types'
import { DEFAULT_FORM_STATUS, DEFAULT_FORM_VALUES } from '@/dex/components/PagePool/Withdraw/utils'
import { curvejsApi } from '@/dex/lib/curvejs'
import type { State } from '@/dex/store/useStore'
import {
  ChainId,
  CurveApi,
  FnStepApproveResponse,
  FnStepEstGasApprovalResponse,
  FnStepResponse,
} from '@/dex/types/main.types'
import { isBonus, isHighSlippage } from '@/dex/utils'
import type { PoolTemplate } from '@curvefi/api/lib/pools'
import { useWallet } from '@evm-ui/features/connect-wallet'
import { shortenAddress } from '@evm-ui/utils'
import { setMissingProvider } from '@evm-ui/utils/store.util'
import type { Nullish } from '@primitives/objects.utils'
import { getErrorMessage } from '@ui/features/errors/errors.util'
import { notify } from '@ui/features/toast/Toast/notify'
import { t } from '@ui/lib/i18n'
import { fetchPoolLpTokenBalance } from '../hooks/usePoolTokenDepositBalances'
import { getTokens } from '../pool.utils'
import { invalidatePoolInfo, invalidateUserPoolInfo } from '../queries/invalidation'

type StateKey = keyof typeof DEFAULT_STATE

type SliceState = {
  activeKey: string
  formEstGas: Record<string, FormEstGas>
  formStatus: FormStatus
  formValues: FormValues
  maxLoading: number | null
  slippage: Record<string, Slippage>
}

const SLICE_KEY = 'poolWithdraw'

type FetchWithdrawProps = {
  activeKey: string
  storedActiveKey: string
  config: Config
  curve: CurveApi
  formType: FormType
  pool: PoolTemplate
  formValues: FormValues
  maxSlippage: string
}

// prettier-ignore
export type PoolWithdrawSlice = {
  [SLICE_KEY]: SliceState & {
    fetchWithdrawToken: (props: FetchWithdrawProps) => Promise<void>
    fetchWithdrawLpToken: (props: FetchWithdrawProps) => Promise<void>
    fetchWithdrawCustom: (props: FetchWithdrawProps) => Promise<void>
    fetchClaimable: (activeKey: string, chainId: ChainId, pool: PoolTemplate) => Promise<void>
    setFormValues: (formType: FormType, config: Config, curve: CurveApi | null, poolId: string, pool: PoolTemplate | undefined, updatedFormValues: Partial<FormValues>, loadMaxAmount: LoadMaxAmount | null, isSeed: boolean | Nullish, maxSlippage: string) => Promise<void>

    // steps
    fetchEstGasApproval: (activeKey: string, config: Config, curve: CurveApi, formType: FormType, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<FnStepEstGasApprovalResponse | undefined>
    fetchStepApprove: (activeKey: string, config: Config, curve: CurveApi, formType: FormType, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<FnStepApproveResponse | undefined>
    fetchStepWithdraw: (activeKey: string, curve: CurveApi, pool: PoolTemplate, formValues: FormValues, maxSlippage: string) => Promise<FnStepResponse | undefined>
    fetchStepUnstake: (activeKey: string, curve: CurveApi, pool: PoolTemplate, formValues: FormValues) => Promise<FnStepResponse | undefined>
    fetchStepClaim: (activeKey: string, curve: CurveApi, pool: PoolTemplate) => Promise<FnStepResponse | undefined>

    setStateByActiveKey: <T>(key: StateKey, activeKey: string, value: T) => void
    setStateByKey: <T>(key: StateKey, value: T) => void
    setStateByKeys: (SliceState: Partial<SliceState>) => void
    resetState: (pool: PoolTemplate, isWrapped: boolean) => void
  }
}

const DEFAULT_STATE: SliceState = {
  activeKey: '',
  formEstGas: {},
  formStatus: DEFAULT_FORM_STATUS,
  formValues: DEFAULT_FORM_VALUES,
  maxLoading: null,
  slippage: {},
}

export const createPoolWithdrawSlice = (
  _: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): PoolWithdrawSlice => ({
  [SLICE_KEY]: {
    ...DEFAULT_STATE,

    fetchWithdrawToken: async props => {
      const { storedActiveKey, config, curve, formType, pool, formValues, maxSlippage } = props
      let activeKey = props.activeKey
      const cFormValues = cloneDeep(formValues)
      const { signerAddress } = curve

      //  get slippage and expected
      if (+cFormValues.lpToken > 0) {
        // set loading state
        cFormValues.amounts = cFormValues.amounts.map(a => ({ ...a, value: '' }))
        get()[SLICE_KEY].setStateByKeys({ formValues: cloneDeep(cFormValues) })
        get()[SLICE_KEY].setStateByKey('slippage', {
          [activeKey]: { ...(get()[SLICE_KEY].slippage[storedActiveKey] ?? DEFAULT_SLIPPAGE), loading: true },
        })

        // update amounts in form value
        const resp = await curvejsApi.poolWithdraw.withdrawOneCoinBonusAndExpected(
          activeKey,
          pool,
          cFormValues.isWrapped,
          cFormValues.lpToken,
          cFormValues.selectedTokenAddress,
        )
        if (resp.activeKey === activeKey) {
          if (resp.error) {
            get()[SLICE_KEY].setStateByKeys({
              slippage: { [activeKey]: { ...DEFAULT_SLIPPAGE, loading: false } },
              formStatus: { ...get()[SLICE_KEY].formStatus, error: resp.error },
            })
          } else {
            cFormValues.amounts = cFormValues.amounts.map(a => ({
              ...a,
              value: a.tokenAddress === cFormValues.selectedTokenAddress ? resp.expected : '',
            }))
            activeKey = getActiveKey(pool.id, formType, cFormValues, maxSlippage)
            get()[SLICE_KEY].setStateByKeys({ activeKey, formValues: cloneDeep(cFormValues) })
            get()[SLICE_KEY].setStateByKey('slippage', {
              [activeKey]: {
                ...DEFAULT_SLIPPAGE,
                slippage: Math.abs(+resp.bonus),
                isHighSlippage: isHighSlippage(+resp.bonus, maxSlippage),
                isBonus: isBonus(+resp.bonus),
              },
            })
          }
        }
      }

      // get gas
      if (signerAddress) {
        void get()[SLICE_KEY].fetchEstGasApproval(activeKey, config, curve, formType, pool, cFormValues, maxSlippage)
      }
    },
    fetchWithdrawLpToken: async props => {
      const { storedActiveKey, config, curve, formType, pool, formValues, maxSlippage } = props
      let activeKey = props.activeKey
      const cFormValues = cloneDeep(formValues)
      const { signerAddress } = curve

      if (+cFormValues.lpToken > 0) {
        // set loading state
        get()[SLICE_KEY].setStateByKey('slippage', {
          [activeKey]: { ...(get()[SLICE_KEY].slippage[storedActiveKey] ?? DEFAULT_SLIPPAGE), loading: true },
        })

        // get expected
        const resp = await curvejsApi.poolWithdraw.withdrawExpected(
          activeKey,
          pool,
          cFormValues.isWrapped,
          cFormValues.lpToken,
        )
        if (resp.error) {
          get()[SLICE_KEY].setStateByKeys({ formStatus: { ...get()[SLICE_KEY].formStatus, error: resp.error } })
        } else {
          cFormValues.amounts = cFormValues.amounts.map((a: Amount, idx) => ({
            ...a,
            value: resp.expected[idx] ?? '0',
          }))
          activeKey = getActiveKey(pool.id, formType, cFormValues, maxSlippage)
          get()[SLICE_KEY].setStateByKeys({
            activeKey,
            formValues: cloneDeep(cFormValues),
            slippage: { [activeKey]: { ...cloneDeep(DEFAULT_SLIPPAGE), slippage: 0 } },
          })
        }

        // get gas
        if (signerAddress) {
          void get()[SLICE_KEY].fetchEstGasApproval(activeKey, config, curve, formType, pool, cFormValues, maxSlippage)
        }
      }
    },
    fetchWithdrawCustom: async props => {
      const { storedActiveKey, config, curve, formType, pool, formValues, maxSlippage } = props
      let activeKey = props.activeKey
      const cFormValues = cloneDeep(formValues)
      const { signerAddress } = curve

      //  get slippage and expected
      if (cFormValues.amounts.some(a => +a.value > 0)) {
        // set loading state
        get()[SLICE_KEY].setStateByKey('slippage', {
          [activeKey]: { ...(get()[SLICE_KEY].slippage[storedActiveKey] ?? DEFAULT_SLIPPAGE), loading: true },
        })

        // update amounts in form value
        const resp = await curvejsApi.poolWithdraw.withdrawImbalanceBonusAndExpected(
          activeKey,
          pool,
          cFormValues.isWrapped,
          parseAmountsForAPI(cFormValues.amounts),
        )

        if (resp.activeKey === activeKey) {
          if (resp.error) {
            get()[SLICE_KEY].setStateByKeys({
              slippage: { [activeKey]: { ...DEFAULT_SLIPPAGE, loading: false } },
              formStatus: { ...get()[SLICE_KEY].formStatus, error: resp.error },
            })
          } else {
            cFormValues.lpToken = resp.expected
            activeKey = getActiveKey(pool.id, formType, cFormValues, maxSlippage)
            get()[SLICE_KEY].setStateByKeys({ activeKey, formValues: cloneDeep(cFormValues) })
            get()[SLICE_KEY].setStateByKey('slippage', {
              [activeKey]: {
                ...DEFAULT_SLIPPAGE,
                slippage: Math.abs(+resp.bonus),
                isHighSlippage: isHighSlippage(+resp.bonus, maxSlippage),
                isBonus: isBonus(+resp.bonus),
              },
            })
          }
        }
      } else if (+cFormValues.lpToken > 0) {
        // set loading state
        get()[SLICE_KEY].setStateByKey('slippage', {
          [activeKey]: { ...(get()[SLICE_KEY].slippage[storedActiveKey] ?? DEFAULT_SLIPPAGE), loading: true },
        })

        // update amounts in form value
        const resp = await curvejsApi.poolWithdraw.withdrawExpected(
          activeKey,
          pool,
          cFormValues.isWrapped,
          cFormValues.lpToken,
        )

        if (resp.activeKey === activeKey) {
          if (resp.error) {
            get()[SLICE_KEY].setStateByKeys({
              slippage: { [activeKey]: { ...DEFAULT_SLIPPAGE, loading: false } },
              formStatus: { ...get()[SLICE_KEY].formStatus, error: resp.error },
            })
          } else {
            cFormValues.amounts = cFormValues.amounts.map((a, idx) => ({ ...a, value: resp.expected[idx] }))
            activeKey = getActiveKey(pool.id, formType, cFormValues, maxSlippage)
            get()[SLICE_KEY].setStateByKeys({ activeKey, formValues: cloneDeep(cFormValues) })
            get()[SLICE_KEY].setStateByKey('slippage', { [activeKey]: { ...cloneDeep(DEFAULT_SLIPPAGE), slippage: 0 } })
          }
        }
      }

      // get gas
      if (signerAddress) {
        void get()[SLICE_KEY].fetchEstGasApproval(activeKey, config, curve, formType, pool, cFormValues, maxSlippage)
      }
    },
    fetchClaimable: async (activeKey, chainId, pool) => {
      const resp = await curvejsApi.poolWithdraw.claimableTokens(activeKey, pool, chainId)
      const storedFormStatus = get()[SLICE_KEY].formStatus

      if (get()[SLICE_KEY].activeKey === resp.activeKey) {
        if (resp.error) {
          get()[SLICE_KEY].setStateByKey('formStatus', { ...storedFormStatus, error: resp.error })
        } else {
          get()[SLICE_KEY].setStateByKey('formStatus', storedFormStatus)
          get()[SLICE_KEY].setStateByKey('formValues', {
            ...get()[SLICE_KEY].formValues,
            claimableCrv: resp.claimableCrv,
            claimableRewards: resp.claimableRewards,
          })
        }
      }
    },
    // eslint-disable-next-line @typescript-eslint/require-await -- Existing violation before enabling this rule.
    setFormValues: async (
      formType,
      config,
      curve,
      poolId,
      pool,
      updatedFormValues,
      _loadMaxAmount,
      isSeed,
      maxSlippage,
    ) => {
      // stored values
      const storedActiveKey = get()[SLICE_KEY].activeKey
      const storedFormValues = get()[SLICE_KEY].formValues
      const storedFormStatus = get()[SLICE_KEY].formStatus

      // update form values
      const cFormValues = cloneDeep({ ...storedFormValues, ...updatedFormValues })
      let activeKey = getActiveKey(poolId, formType, cFormValues, maxSlippage)
      get()[SLICE_KEY].setStateByKeys({
        activeKey,
        formStatus: {
          ...DEFAULT_FORM_STATUS,
          isApproved: storedFormStatus.isApproved,
          isClaimCrv: storedFormStatus.isClaimCrv,
          isClaimRewards: storedFormStatus.isClaimRewards,
        },
        formValues: cloneDeep(cFormValues),
      })

      if (!curve || !pool || isSeed || cFormValues.isWrapped === null) return

      const { chainId, signerAddress } = curve
      const { tokens, tokenAddresses } = getTokens(pool, { wrapped: cFormValues.isWrapped })

      if (formType === 'WITHDRAW') {
        // set default selected if it is empty
        if (!cFormValues.selected && +cFormValues.lpToken > 0) {
          cFormValues.selected = 'token'
          cFormValues.selectedToken = tokens[0]
          cFormValues.selectedTokenAddress = tokenAddresses[0]
          activeKey = getActiveKey(poolId, formType, cFormValues, maxSlippage)
          get()[SLICE_KEY].setStateByKeys({ activeKey, formValues: cloneDeep(cFormValues) })
        }

        const props: FetchWithdrawProps = {
          activeKey,
          storedActiveKey,
          config,
          curve,
          formType,
          pool,
          formValues: cFormValues,
          maxSlippage,
        }
        if (cFormValues.selected === 'token') {
          void get()[SLICE_KEY].fetchWithdrawToken(props)
        } else if (cFormValues.selected === 'lpToken') {
          void get()[SLICE_KEY].fetchWithdrawLpToken(props)
        } else if (cFormValues.selected === 'imbalance') {
          void get()[SLICE_KEY].fetchWithdrawCustom(props)
        }
      } else if (formType === 'UNSTAKE' && !!signerAddress && +cFormValues.stakedLpToken > 0) {
        void get()[SLICE_KEY].fetchEstGasApproval(activeKey, config, curve, formType, pool, cFormValues, maxSlippage)
      } else if (formType === 'CLAIM') {
        void get()[SLICE_KEY].fetchClaimable(activeKey, chainId, pool)
      }
    },

    // steps
    fetchEstGasApproval: async (activeKey, config, curve, formType, pool, formValues, maxSlippage) => {
      const { chainId } = curve
      let resp
      try {
        if (formType === 'WITHDRAW') {
          const lpTokenBalance = await fetchPoolLpTokenBalance(config, curve, getAddress(pool.address))

          if (+formValues.lpToken > 0 && +lpTokenBalance >= +formValues.lpToken) {
            resp =
              formValues.selected === 'lpToken'
                ? await curvejsApi.poolWithdraw.withdrawEstGasApproval(
                    activeKey,
                    chainId,
                    pool,
                    formValues.isWrapped,
                    formValues.lpToken,
                    maxSlippage,
                  )
                : formValues.selected === 'imbalance'
                  ? await curvejsApi.poolWithdraw.withdrawImbalanceEstGasApproval(
                      activeKey,
                      chainId,
                      pool,
                      formValues.isWrapped,
                      parseAmountsForAPI(formValues.amounts),
                      maxSlippage,
                    )
                  : await curvejsApi.poolWithdraw.withdrawOneCoinEstGasApproval(
                      activeKey,
                      chainId,
                      pool,
                      formValues.isWrapped,
                      formValues.lpToken,
                      formValues.selectedTokenAddress,
                      maxSlippage,
                    )
          }
        } else if (formType === 'UNSTAKE') {
          const fn = curvejsApi.poolWithdraw.unstakeEstGas
          resp = await fn(activeKey, chainId, pool, formValues.stakedLpToken)
        }
      } catch (error) {
        resp = {
          activeKey,
          isApproved: false,
          estimatedGas: null,
          error: getErrorMessage(error, 'error-est-gas-approval'),
        }
      }

      if (resp) {
        // set estimate gas state
        get()[SLICE_KEY].setStateByKey('formEstGas', {
          ...get()[SLICE_KEY].formEstGas,
          [activeKey]: { estimatedGas: resp.estimatedGas, loading: false },
        })

        // set form status
        const storedFormStatus = get()[SLICE_KEY].formStatus
        if (activeKey === get()[SLICE_KEY].activeKey && !storedFormStatus.formProcessing) {
          get()[SLICE_KEY].setStateByKey('formStatus', {
            ...storedFormStatus,
            isApproved: resp.isApproved,
            error: resp.error || storedFormStatus.error,
          })
        }
      }
      return resp
    },
    fetchStepApprove: async (activeKey, config, curve, formType, pool, formValues, maxSlippage) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'APPROVAL',
      })
      const { amounts, lpToken, selected } = formValues
      let resp
      if (selected === 'token' || selected === 'lpToken') {
        const fn =
          selected === 'token'
            ? curvejsApi.poolWithdraw.withdrawOneCoinApprove
            : curvejsApi.poolWithdraw.withdrawApprove
        resp = await fn(activeKey, provider, pool, lpToken)
      } else if (formValues.selected === 'imbalance') {
        const fn = curvejsApi.poolWithdraw.withdrawImbalanceApprove
        resp = await fn(activeKey, provider, pool, parseAmountsForAPI(amounts))
      }
      if (resp?.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.step = ''
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'APPROVE'
          cFormStatus.isApproved = true
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)

          // fetch est gas and approval
          await get()[SLICE_KEY].fetchEstGasApproval(activeKey, config, curve, formType, pool, formValues, maxSlippage)
        }

        return resp
      }
    },
    fetchStepWithdraw: async (activeKey, curve, pool, formValues, maxSlippage) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'WITHDRAW',
      })
      let resp
      if (formValues.selected === 'token') {
        const fn = curvejsApi.poolWithdraw.withdrawOneCoin
        resp = await fn(
          activeKey,
          provider,
          pool,
          formValues.isWrapped,
          formValues.lpToken,
          formValues.selectedTokenAddress,
          maxSlippage,
        )
      } else if (formValues.selected === 'lpToken') {
        const fn = curvejsApi.poolWithdraw.withdraw
        resp = await fn(activeKey, provider, pool, formValues.isWrapped, formValues.lpToken, maxSlippage)
      } else if (formValues.selected === 'imbalance') {
        const amounts = parseAmountsForAPI(formValues.amounts)
        const fn = curvejsApi.poolWithdraw.withdrawImbalance
        resp = await fn(activeKey, provider, pool, formValues.isWrapped, amounts, maxSlippage)
      }
      if (resp?.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'WITHDRAW'
          get()[SLICE_KEY].setStateByKeys({ formStatus: cFormStatus, formValues: resetFormValues(formValues) })

          const params = {
            chainId: curve.chainId,
            poolAddress: getAddress(pool.address),
            userAddress: curve.signerAddress,
          }
          await Promise.all([invalidateUserPoolInfo(params), invalidatePoolInfo(params)]).catch(error =>
            notify(
              t`Transaction succeeded, but balances could not be refreshed.` + ' ' + getErrorMessage(error),
              'error',
            ),
          )
        }

        return resp
      }
    },
    fetchStepUnstake: async (activeKey, curve, pool, formValues) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'UNSTAKE',
      })
      const resp = await curvejsApi.poolWithdraw.unstake(activeKey, provider, pool, formValues.stakedLpToken)
      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = 'UNSTAKE'
          get()[SLICE_KEY].setStateByKeys({ formStatus: cFormStatus, formValues: resetFormValues(formValues) })

          const params = {
            chainId: curve.chainId,
            poolAddress: getAddress(pool.address),
            userAddress: curve.signerAddress,
          }
          await Promise.all([invalidateUserPoolInfo(params), invalidatePoolInfo(params)]).catch(error =>
            notify(
              t`Transaction succeeded, but balances could not be refreshed.` + ' ' + getErrorMessage(error),
              'error',
            ),
          )
        }

        return resp
      }
    },
    fetchStepClaim: async (activeKey, curve, pool) => {
      const { provider } = useWallet.getState()
      if (!provider) return setMissingProvider(get()[SLICE_KEY])

      get()[SLICE_KEY].setStateByKey('formStatus', {
        ...get()[SLICE_KEY].formStatus,
        formProcessing: true,
        step: 'CLAIM',
      })
      const { isClaimCrv } = get()[SLICE_KEY].formStatus
      const resp = isClaimCrv
        ? await curvejsApi.poolWithdraw.claimCrv(activeKey, provider, pool)
        : await curvejsApi.poolWithdraw.claimRewards(activeKey, provider, pool)
      if (resp.activeKey === get()[SLICE_KEY].activeKey) {
        const cFormStatus = cloneDeep(get()[SLICE_KEY].formStatus)
        cFormStatus.formProcessing = false
        cFormStatus.step = ''
        cFormStatus.isClaimCrv = false
        cFormStatus.isClaimRewards = false
        cFormStatus.error = ''

        if (resp.error) {
          cFormStatus.error = resp.error
          get()[SLICE_KEY].setStateByKey('formStatus', cFormStatus)
        } else {
          cFormStatus.formTypeCompleted = isClaimCrv ? 'CLAIM_CRV' : 'CLAIM_REWARDS'
          const storedFormValues = get()[SLICE_KEY].formValues
          get()[SLICE_KEY].setStateByKeys({ formStatus: cFormStatus, formValues: resetFormValues(storedFormValues) })

          const params = {
            chainId: curve.chainId,
            poolAddress: getAddress(pool.address),
            userAddress: curve.signerAddress,
          }
          await Promise.all([invalidateUserPoolInfo(params), invalidatePoolInfo(params)])
        }

        return resp
      }
    },

    // slice helpers
    setStateByActiveKey: (key, activeKey, value) => {
      const foundKey = get()[SLICE_KEY][key] as object
      if (Object.keys(foundKey).length > 30) {
        get().setAppStateByKey(SLICE_KEY, key, { [activeKey]: value })
      } else {
        get().setAppStateByActiveKey(SLICE_KEY, key, activeKey, value)
      }
    },
    setStateByKey: (key, value) => {
      get().setAppStateByKey(SLICE_KEY, key, value)
    },
    setStateByKeys: sliceState => {
      get().setAppStateByKeys(SLICE_KEY, sliceState)
    },
    resetState: (pool, isWrapped) => {
      const { tokens, tokenAddresses } = getTokens(pool, { wrapped: isWrapped })
      get().resetAppState(SLICE_KEY, {
        ...DEFAULT_STATE,
        formValues: {
          ...DEFAULT_FORM_VALUES,
          isWrapped,
          amounts: tokens.map((token, idx) => ({ token, tokenAddress: tokenAddresses[idx], value: '' })),
        },
      })
    },
  },
})

function getActiveKey(
  poolId: string,
  formType: FormType,
  {
    amounts,
    claimableCrv,
    claimableRewards,
    isWrapped,
    lpToken,
    stakedLpToken,
    selected,
    selectedToken,
    selectedTokenAddress,
  }: FormValues,
  maxSlippage: string,
) {
  let activeKey = `${formType}-${poolId}-`

  if (formType === 'WITHDRAW') {
    if (selected === 'token') {
      const selectedStr = `${selected}-${selectedToken}-${
        selectedTokenAddress ? shortenAddress(selectedTokenAddress) : ''
      }`
      activeKey += `${selectedStr}-${isWrapped}-${maxSlippage}-${lpToken}`
    } else if (selected === 'lpToken') {
      activeKey += `${selected}-${isWrapped}-${maxSlippage}-${lpToken}`
    } else {
      const amountsStr = amounts.map(a => a.value).join('-')
      activeKey += `${selected}-${amountsStr}`
    }
  } else if (formType === 'UNSTAKE') {
    activeKey += `${stakedLpToken}`
  } else if (formType === 'CLAIM') {
    // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions -- Existing violation before enabling this rule.
    activeKey += `${claimableCrv}-${claimableRewards}`
  }
  return activeKey
}

const resetFormValues = (formValues: FormValues) => ({
  ...cloneDeep(formValues),
  amounts: formValues.amounts.map(a => ({ ...a, value: '' })),
  claimableRewards: [],
  claimableCrv: '',
  lpToken: '',
  stakedLpToken: '',
})
